// src/components/pages/b2b/News/healthInformationContent.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Container,
  Breadcrumbs,
  Link,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import { getLangFromCookie } from "../../../../Utils/LangCookie";
import { apiReadNews } from "../../../../api/News";
import handwashGuide from "../../../../data/handwashGuide.json";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";

const I18N = {
  home: { ZH: "首頁", EN: "Home", JP: "ホーム" },
  info: { ZH: "最新資訊", EN: "Information", JP: "最新情報" },
  healthColumn: { ZH: "衛生資訊", EN: "Health Articles", JP: "衛生情報コラム" },
};

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

/*
 * Reuse one YouTube IFrame API request across the page.
 *
 * This prevents duplicate API scripts or multiple loading promises
 * when React rerenders the component.
 */
let youtubeIframeApiPromise = null;

function getText(value, lang, fallback = "") {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  return value[lang] || value.ZH || value.EN || value.JP || fallback;
}

function getPublicAssetUrl(path = "") {
  const cleanPath = String(path || "");
  if (!cleanPath) return "";
  if (/^https?:\/\//i.test(cleanPath)) return cleanPath;
  return `${process.env.PUBLIC_URL || ""}${cleanPath.startsWith("/") ? "" : "/"}${cleanPath}`;
}

function isValidYouTubeVideoId(value = "") {
  return /^[a-zA-Z0-9_-]{11}$/.test(String(value || "").trim());
}

/*
 * Extract a YouTube video ID from the common URL formats:
 *
 * https://www.youtube.com/watch?v=VIDEO_ID
 * https://youtu.be/VIDEO_ID
 * https://www.youtube.com/embed/VIDEO_ID
 * https://www.youtube.com/shorts/VIDEO_ID
 * https://www.youtube.com/live/VIDEO_ID
 *
 * A raw eleven-character video ID is also accepted.
 */
function getYouTubeVideoId(value = "") {
  const rawValue = String(value || "").trim();

  if (!rawValue) {
    return "";
  }

  if (isValidYouTubeVideoId(rawValue)) {
    return rawValue;
  }

  try {
    const parsedUrl = new URL(rawValue);

    const hostname = parsedUrl.hostname
      .toLowerCase()
      .replace(/^www\./, "")
      .replace(/^m\./, "");

    if (hostname === "youtu.be") {
      const shortVideoId =
        parsedUrl.pathname.split("/").filter(Boolean)[0] || "";

      return isValidYouTubeVideoId(shortVideoId) ? shortVideoId : "";
    }

    const isYouTubeDomain =
      hostname === "youtube.com" ||
      hostname.endsWith(".youtube.com") ||
      hostname === "youtube-nocookie.com" ||
      hostname.endsWith(".youtube-nocookie.com");

    if (!isYouTubeDomain) {
      return "";
    }

    const watchVideoId = parsedUrl.searchParams.get("v") || "";

    if (isValidYouTubeVideoId(watchVideoId)) {
      return watchVideoId;
    }

    const pathParts = parsedUrl.pathname.split("/").filter(Boolean);

    const supportedPathTypes = ["embed", "shorts", "live", "v"];

    if (pathParts.length >= 2 && supportedPathTypes.includes(pathParts[0])) {
      const pathVideoId = pathParts[1] || "";

      return isValidYouTubeVideoId(pathVideoId) ? pathVideoId : "";
    }
  } catch {
    /*
     * Invalid or incomplete URLs return an empty ID.
     * The video section will then remain hidden.
     */
  }

  return "";
}

function getYouTubeThumbnailUrl(videoId = "") {
  const cleanVideoId = String(videoId || "").trim();

  if (!isValidYouTubeVideoId(cleanVideoId)) {
    return "";
  }

  return `https://i.ytimg.com/vi/${cleanVideoId}/hqdefault.jpg`;
}

/*
 * Load the official YouTube IFrame Player API once.
 *
 * The resolved value is window.YT, which will later be used to
 * create the viewport-controlled player.
 */
function loadYouTubeIframeAPI() {
  if (typeof window === "undefined") {
    return Promise.reject(
      new Error("The YouTube IFrame API requires a browser environment."),
    );
  }

  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }

  if (youtubeIframeApiPromise) {
    return youtubeIframeApiPromise;
  }

  youtubeIframeApiPromise = new Promise((resolve, reject) => {
    const previousReadyHandler = window.onYouTubeIframeAPIReady;

    const rejectApiLoad = () => {
      youtubeIframeApiPromise = null;

      reject(new Error("The YouTube IFrame API could not be loaded."));
    };

    window.onYouTubeIframeAPIReady = () => {
      try {
        if (typeof previousReadyHandler === "function") {
          previousReadyHandler();
        }
      } finally {
        if (window.YT?.Player) {
          resolve(window.YT);
        } else {
          rejectApiLoad();
        }
      }
    };

    const existingScript = document.querySelector(
      'script[src="https://www.youtube.com/iframe_api"]',
    );

    if (existingScript) {
      existingScript.addEventListener("error", rejectApiLoad, {
        once: true,
      });

      return;
    }

    const script = document.createElement("script");

    script.src = "https://www.youtube.com/iframe_api";

    script.async = true;

    script.addEventListener("error", rejectApiLoad, {
      once: true,
    });

    document.head.appendChild(script);
  });

  return youtubeIframeApiPromise;
}

function getManualImageUrl(image = "") {
  if (!image) return "";

  const cleanImage = String(image || "").trim();

  if (!cleanImage) return "";

  if (/^https?:\/\//i.test(cleanImage)) {
    return cleanImage;
  }

  if (cleanImage.startsWith("/")) {
    return getPublicAssetUrl(cleanImage);
  }

  const base = handwashGuide.imageBase || "";
  return getPublicAssetUrl(`${base.replace(/\/+$/, "")}/${cleanImage}`);
}

function getSlugFromPathname(pathname = "") {
  const clean = String(pathname || "")
    .split("?")[0]
    .split("#")[0];
  const parts = clean.split("/").filter(Boolean);
  const last = parts[parts.length - 1] || "";
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

function resolveUrlMaybe(relativeOrAbsolute) {
  const u = relativeOrAbsolute || "";
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("blob:") || u.startsWith("data:")) return u;
  if (u.startsWith("//")) return `https:${u}`;
  if (u.startsWith("/")) return `${API_BASE}${u}`;
  return API_BASE ? `${API_BASE}/${u}` : u;
}

function normalizeHtmlForRender(html) {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(String(html), "text/html");

    doc.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src") || "";
      const fixedSrc = resolveUrlMaybe(src);
      if (fixedSrc) img.setAttribute("src", fixedSrc);

      const wRaw = img.getAttribute("width");
      const wNum =
        wRaw != null && String(wRaw).trim() !== "" ? Number(wRaw) : NaN;
      const style = img.getAttribute("style") || "";

      if (!Number.isNaN(wNum) && wNum > 0 && wNum <= 100) {
        const cleaned = style.replace(/(^|;)\s*width\s*:\s*[^;]+;?/gi, "$1");
        img.setAttribute(
          "style",
          `${cleaned}${
            cleaned.trim() && !cleaned.trim().endsWith(";") ? ";" : ""
          }width:${wNum}%;height:auto;`,
        );
        img.removeAttribute("width");
      }

      const align = (img.getAttribute("align") || "").toLowerCase();
      if (align) {
        const s2 = img.getAttribute("style") || "";
        let next = s2;
        if (!/display\s*:/i.test(next)) {
          next += `${
            next.trim().endsWith(";") || !next.trim() ? "" : ";"
          }display:block;`;
        }

        if (align === "left") next += "margin:10px auto 10px 0;";
        else if (align === "right") next += "margin:10px 0 10px auto;";
        else next += "margin:10px auto;";

        img.setAttribute("style", next);
      }
    });

    doc.querySelectorAll("a").forEach((a) => {
      const href = a.getAttribute("href") || "";
      const fixed = resolveUrlMaybe(href);
      if (fixed) a.setAttribute("href", fixed);
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });

    return doc.body.innerHTML;
  } catch {
    return String(html || "");
  }
}

function isHandSanitizingManual({ slug, news }) {
  const raw = `${slug || ""} ${news?.title || ""}`.toLowerCase();
  const matches = Array.isArray(handwashGuide.match) ? handwashGuide.match : [];

  return matches.some((keyword) =>
    raw.includes(String(keyword || "").toLowerCase()),
  );
}

function ManualStepCard({ step, lang, color }) {
  const text = getText(step.text, lang);
  const imageUrl = getManualImageUrl(step.image);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "32px 1fr",
        gap: 1,
        alignItems: "start",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          bgcolor: color,
          color: "#fff",
          fontSize: 13,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 0.25,
          flexShrink: 0,
        }}
      >
        {step.no}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={text}
            sx={{
              width: "100%",
              aspectRatio: "16 / 10",
              objectFit: "cover",
              border: `2px solid ${color}`,
              borderRadius: 1,
              bgcolor: "#f5f5f5",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              aspectRatio: "16 / 10",
              border: `2px dashed ${color}`,
              borderRadius: 1,
              bgcolor: "#f8f8f8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              fontSize: 12,
              textAlign: "center",
              px: 1,
            }}
          >
            No image
          </Box>
        )}

        <Typography
          sx={{
            mt: 0.75,
            fontSize: 15,
            fontWeight: 400,
            lineHeight: 1.45,
            wordBreak: "break-word",
          }}
        >
          {text}
        </Typography>
      </Box>
    </Box>
  );
}

function ManualSection({ section, lang }) {
  const color = section.color || "#005F91";
  const steps = Array.isArray(section.steps) ? section.steps : [];

  return (
    <Box
      sx={{
        display: "grid",

        /*
         * Mobile remains stacked.
         *
         * Desktop gives the localized section title a protected
         * column that cannot overlap the procedure-step grid.
         */
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          md: "180px minmax(0, 1fr)",
          lg: "220px minmax(0, 1fr)",
        },

        rowGap: {
          xs: 2,
          md: 0,
        },

        columnGap: {
          md: 4,
          lg: 5,
        },

        alignItems: "start",

        mb: {
          xs: 4,
          md: 5,
        },

        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",

          color,

          fontWeight: 700,

          fontSize: {
            xs: 24,
            md: 28,
          },

          lineHeight: 1.25,

          /*
           * Long English or Japanese titles may wrap inside the
           * protected title column instead of extending into the
           * first step-number badge.
           */
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          wordBreak: "normal",

          alignSelf: "start",
        }}
      >
        {getText(section.title, lang)}
      </Typography>

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },

          gap: {
            xs: 2,
            md: 2.5,
          },

          width: "100%",
          minWidth: 0,
        }}
      >
        {steps.map((step) => (
          <ManualStepCard
            key={`${section.key}-${step.no}`}
            step={step}
            lang={lang}
            color={color}
          />
        ))}
      </Box>
    </Box>
  );
}
function FoodWorkerGuide({ lang }) {
  const data = handwashGuide.foodWorker || {};
  const flowItems = Array.isArray(data.flow) ? data.flow : [];
  const labels = data.labels || {};

  const getFlowLabel = (key) => getText(labels[key], lang);

  return (
    <Box
      sx={{
        mt: 2,
        px: { xs: 1.5, md: 2.25 },
        py: { xs: 1.75, md: 2 },
        borderRadius: 1.5,
        bgcolor: "#eef4f8",
        border: "1px solid #d8e4ef",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "180px 1fr" },
          gap: { xs: 1.25, md: 2 },
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: 56,
            px: 2,
            border: "2px solid #1267A8",
            borderRadius: 1,
            color: getPrimaryColor("b2b"),
            bgcolor: "#fff",
            fontWeight: 700,
            fontSize: 18,
            whiteSpace: "nowrap",
          }}
        >
          {getText(data.title, lang)}
        </Typography>

        <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.7 }}>
          {getText(data.description, lang)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "1.65fr 1.65fr 0.82fr 0.82fr",
          },
          gap: {
            xs: 2,
            sm: 2.5,
            md: 2.25,
          },
          alignItems: "stretch",
        }}
      >
        {flowItems.map((item, index) => (
          <Box
            key={item.key}
            sx={{
              position: "relative",
              bgcolor: item.color,
              borderRadius: 1.25,
              px: 1.25,
              pt: 1,
              pb: 2,
              minHeight: {
                xs: 180,
                sm: 190,
                md: 200,
              },
              color: "#fff",
              minWidth: 0,
              "&::after":
                index < flowItems.length - 1
                  ? {
                      content: '""',
                      display: { xs: "none", md: "block" },
                      position: "absolute",
                      top: "50%",
                      right: -20,
                      transform: "translateY(-50%)",
                      width: 0,
                      height: 0,
                      borderTop: "13px solid transparent",
                      borderBottom: "13px solid transparent",
                      borderLeft: "15px solid #b5b5b5",
                      zIndex: 2,
                    }
                  : {},
            }}
          >
            <Typography
              sx={{
                textAlign: "center",
                fontSize: 20,
                fontWeight: 700,
                lineHeight: 1,
                mb: 1,
              }}
            >
              {getFlowLabel(item.type)}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  item.images?.length === 2 ? "1fr 1fr" : "1fr",
                gap: {
                  xs: 1,
                  sm: 1.5,
                  md: 0.75,
                },
                alignItems: "start",
              }}
            >
              {(item.images || []).map((image, imageIndex) => (
                <Box key={`${item.key}-${imageIndex}`} sx={{ minWidth: 0 }}>
                  <Box
                    component="img"
                    src={getManualImageUrl(image.image)}
                    alt={getFlowLabel(image.captionKey)}
                    sx={{
                      width: "100%",
                      height: "auto",
                      aspectRatio:
                        item.images?.length === 2 ? "4 / 3" : "16 / 10",
                      objectFit: "cover",
                      display: "block",
                      bgcolor: "#fff",
                    }}
                  />

                  <Typography
                    sx={{
                      mt: 0.35,
                      color: "#fff",
                      fontSize: 15,
                      fontWeight: 500,
                      lineHeight: 1.1,
                    }}
                  >
                    {getFlowLabel(image.captionKey)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ManualThumbnailImage({ lang }) {
  const thumbnail = handwashGuide.thumbnail || {};
  const imageUrl = getManualImageUrl(thumbnail.image);

  if (!imageUrl) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        component="img"
        src={imageUrl}
        alt={getText(thumbnail.alt, lang)}
        sx={{
          width: "100%",
          maxHeight: "auto",
          objectFit: "contain",
          borderRadius: 2,
          display: "block",
        }}
      />
    </Box>
  );
}

/*
 * YouTube handwashing demonstration.
 *
 * Behavior:
 *
 * 1. The video remains stopped until the player frame enters
 *    the viewport.
 * 2. The player is muted before automatic playback because browsers
 *    generally prevent autoplay with sound.
 * 3. The same video loops continuously.
 * 4. Normal YouTube controls remain available.
 * 5. After playback begins, a pause is treated as the user's choice.
 * 6. Scrolling away and returning will not restart a paused video.
 */
function ManualYouTubeVideoSection({ lang }) {
  const videoData = handwashGuide.video || {};

  const videoId = getYouTubeVideoId(
    videoData.youtubeUrl || videoData.url || videoData.videoId || "",
  );

  const videoTitle = getText(
    videoData.title,
    lang,
    "Handwashing demonstration",
  );

  /*
   * State used by the custom video interface.
   *
   * The next update will use this to show no controls while playing
   * and one centered resume button while paused.
   */
  const [playerUiState, setPlayerUiState] = useState({
    ready: false,
    playing: false,
    paused: false,
    muted: false,
    showCleanCover: true,
  });

  const viewportRef = useRef(null);

  const playerMountRef = useRef(null);

  const playerRef = useRef(null);

  const observerRef = useRef(null);

  const isPlayerReadyRef = useRef(false);

  const isVisibleRef = useRef(false);

  const autoStartPendingRef = useRef(false);

  const hasStartedPlayingRef = useRef(false);

  const userPausedRef = useRef(false);

  const cleanCoverTimerRef = useRef(null);

  /*
   * Keep the generated YouTube iframe's accessible title synchronized
   * when the website language changes.
   */
  useEffect(() => {
    const iframe = playerRef.current?.getIframe?.();

    if (iframe && videoTitle) {
      iframe.setAttribute("title", videoTitle);
    }
  }, [videoTitle]);

  useEffect(() => {
    const viewportElement = viewportRef.current;

    const mountElement = playerMountRef.current;

    if (!videoId || !viewportElement || !mountElement) {
      return undefined;
    }

    let effectCancelled = false;

    isPlayerReadyRef.current = false;

    isVisibleRef.current = false;

    autoStartPendingRef.current = false;

    hasStartedPlayingRef.current = false;

    userPausedRef.current = false;

    setPlayerUiState({
      ready: false,
      playing: false,
      paused: false,
      muted: false,
      showCleanCover: true,
    });

    /*
     * Start the player only after both conditions are satisfied:
     *
     * - the YouTube player is ready;
     * - the player frame has entered the viewport.
     *
     * Mobile browsers usually block autoplay with sound, so viewport
     * autoplay must start muted.
     */
    const tryViewportAutoPlay = () => {
      const player = playerRef.current;

      if (
        effectCancelled ||
        !player ||
        !isPlayerReadyRef.current ||
        !isVisibleRef.current ||
        autoStartPendingRef.current ||
        hasStartedPlayingRef.current ||
        userPausedRef.current
      ) {
        return;
      }

      autoStartPendingRef.current = true;

      try {
        player.mute();
        player.setVolume(100);

        setPlayerUiState((current) => ({
          ...current,
          muted: true,
        }));

        player.playVideo();
      } catch {
        autoStartPendingRef.current = false;
      }
    };

    if (typeof IntersectionObserver !== "undefined") {
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          isVisibleRef.current = Boolean(entry?.isIntersecting);

          if (isVisibleRef.current) {
            tryViewportAutoPlay();
          }
        },
        {
          threshold: [0, 0.01, 0.1, 0.35, 1],

          rootMargin: "0px",
        },
      );

      observerRef.current.observe(viewportElement);
    }

    loadYouTubeIframeAPI()
      .then((YT) => {
        if (effectCancelled || !playerMountRef.current) {
          return;
        }

        playerRef.current = new YT.Player(playerMountRef.current, {
          width: "100%",
          height: "100%",

          videoId,

          playerVars: {
            autoplay: 0,

            /*
             * Hide YouTube's native HUD/control bar.
             *
             * The page provides a clean hover-only custom control
             * layer with play/pause and sound buttons.
             */
            controls: 0,
            disablekb: 1,
            fs: 0,

            playsinline: 1,

            /*
             * Disable automatically displayed annotations and
             * captions. Captions can still be part of the source
             * video itself.
             */
            iv_load_policy: 3,
            cc_load_policy: 0,

            /*
             * For one-video looping, YouTube requires the same
             * video ID in the playlist parameter.
             */
            loop: 1,
            playlist: videoId,

            rel: 0,
          },

          events: {
            onReady: (event) => {
              if (effectCancelled) {
                return;
              }

              isPlayerReadyRef.current = true;

              setPlayerUiState((current) => ({
                ...current,
                ready: true,
              }));

              const iframe = event.target?.getIframe?.();

              if (iframe && videoTitle) {
                iframe.setAttribute("title", videoTitle);
              }

              tryViewportAutoPlay();
            },

            onStateChange: (event) => {
              if (effectCancelled) {
                return;
              }

              if (event.data === YT.PlayerState.PLAYING) {
                hasStartedPlayingRef.current = true;

                autoStartPendingRef.current = false;

                userPausedRef.current = false;

                setPlayerUiState({
                  ready: true,
                  playing: true,
                  paused: false,
                  muted:
                    typeof event.target?.isMuted === "function"
                      ? event.target.isMuted()
                      : false,
                  showCleanCover: true,
                });

                if (cleanCoverTimerRef.current) {
                  clearTimeout(cleanCoverTimerRef.current);
                }

                cleanCoverTimerRef.current = window.setTimeout(() => {
                  setPlayerUiState((current) => ({
                    ...current,
                    showCleanCover: false,
                  }));
                }, 1600);

                /*
                 * Automatic viewport playback is needed only once.
                 * Disconnecting prevents viewport re-entry from
                 * restarting the video after a manual pause.
                 */
                observerRef.current?.disconnect();

                return;
              }

              if (
                event.data === YT.PlayerState.PAUSED &&
                hasStartedPlayingRef.current
              ) {
                userPausedRef.current = true;

                setPlayerUiState((current) => ({
                  ...current,
                  ready: true,
                  playing: false,
                  paused: true,
                  muted:
                    typeof event.target?.isMuted === "function"
                      ? event.target.isMuted()
                      : current.muted,
                  showCleanCover: false,
                }));

                observerRef.current?.disconnect();

                return;
              }

              if (event.data === YT.PlayerState.BUFFERING) {
                setPlayerUiState((current) => ({
                  ...current,
                  playing: false,
                }));

                return;
              }

              /*
               * The playlist setting normally performs the loop.
               * This remains as a fallback if YouTube reports the
               * ended state without automatically restarting.
               */
              if (
                event.data === YT.PlayerState.ENDED &&
                !userPausedRef.current
              ) {
                setPlayerUiState((current) => ({
                  ...current,
                  ready: true,
                  playing: false,
                  paused: false,
                  muted:
                    typeof event.target?.isMuted === "function"
                      ? event.target.isMuted()
                      : current.muted,
                  showCleanCover: false,
                }));

                try {
                  event.target.seekTo(0, true);

                  event.target.playVideo();
                } catch {
                  // The standard YouTube loop remains available.
                }
              }
            },

            onError: () => {
              autoStartPendingRef.current = false;
            },
          },
        });
      })
      .catch((error) => {
        console.error(
          "[ManualYouTubeVideoSection] YouTube player load failed:",
          error,
        );
      });

    return () => {
      effectCancelled = true;

      observerRef.current?.disconnect();
      observerRef.current = null;

      if (cleanCoverTimerRef.current) {
        clearTimeout(cleanCoverTimerRef.current);
        cleanCoverTimerRef.current = null;
      }

      try {
        playerRef.current?.destroy?.();
      } catch {
        /*
         * The player may already have been removed during page
         * navigation or a React development-mode remount.
         */
      }

      playerRef.current = null;

      isPlayerReadyRef.current = false;

      isVisibleRef.current = false;
    };
  }, [videoId]);

  const handlePlayerInteraction = () => {
    const player = playerRef.current;

    if (!player || !playerUiState.ready) {
      return;
    }

    try {
      if (playerUiState.playing) {
        /*
         * Clicking the clear video surface pauses playback.
         *
         * The PAUSED state event will update the visible custom
         * resume button.
         */
        userPausedRef.current = true;

        player.pauseVideo();

        return;
      }

      if (playerUiState.paused) {
        /*
         * A deliberate resume clears the manual-pause flag.
         */
        userPausedRef.current = false;

        player.playVideo();

        return;
      }

      /*
       * Also allow manual playback when autoplay has not started,
       * such as when the browser rejected the automatic request.
       */
      userPausedRef.current = false;

      player.unMute();
      player.setVolume(100);

      setPlayerUiState((current) => ({
        ...current,
        muted: false,
      }));

      player.playVideo();
    } catch {
      // Keep the page usable if the player is temporarily unavailable.
    }
  };

  const handleSoundToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const player = playerRef.current;

    if (!player || !playerUiState.ready) {
      return;
    }

    try {
      if (playerUiState.muted) {
        player.unMute();
        player.setVolume(100);

        setPlayerUiState((current) => ({
          ...current,
          muted: false,
        }));

        return;
      }

      player.mute();

      setPlayerUiState((current) => ({
        ...current,
        muted: true,
      }));
    } catch {
      // Keep the page usable if the player is temporarily unavailable.
    }
  };

  /*
   * Do not render an empty player area until handwashGuide.json
   * contains a valid YouTube URL or eleven-character video ID.
   */
  if (!videoId) {
    return null;
  }

  return (
    <Box
      ref={viewportRef}
      aria-busy={!playerUiState.ready}
      data-player-ready={playerUiState.ready ? "true" : "false"}
      data-player-playing={playerUiState.playing ? "true" : "false"}
      data-player-paused={playerUiState.paused ? "true" : "false"}
      sx={{
        width: "100%",

        display: "flex",
        justifyContent: "center",

        mt: {
          xs: 1,
          md: 2,
        },

        mb: {
          xs: 4,
          md: 5,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",

          width: "100%",
          maxWidth: 960,

          aspectRatio: "16 / 9",

          overflow: "hidden",

          bgcolor: "#000000",

          borderRadius: {
            xs: 1.5,
            md: 2,
          },

          boxShadow: "0 18px 42px rgba(0, 61, 94, 0.14)",

          "@media (hover: hover) and (pointer: fine)": {
            "&:hover .manual-youtube-controls": {
              opacity: 1,
              pointerEvents: "auto",
            },
          },

          /*
           * The IFrame API replaces the mount element with a YouTube
           * iframe. Keep the generated iframe fitted to this wrapper.
           */
          "& iframe": {
            position: "absolute",

            inset: 0,

            display: "block",

            width: "100%",
            height: "100%",

            border: 0,

            /*
             * Keep the video clean by preventing YouTube's native
             * hover/click HUD from appearing.
             */
            pointerEvents: "none",
          },
        }}
      >
        <Box
          ref={playerMountRef}
          sx={{
            position: "absolute",
            inset: 0,

            width: "100%",
            height: "100%",
          }}
        />

        {/* Clean cover hides YouTube's startup HUD */}
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,

            zIndex: 2,

            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.08)), url(${getYouTubeThumbnailUrl(videoId)})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center center",

            opacity: playerUiState.showCleanCover ? 1 : 0,

            transition: "opacity 320ms ease",

            pointerEvents: "none",
          }}
        />

        {/* Clean hover-only custom controls */}
        <Box
          className="manual-youtube-controls"
          sx={{
            position: "absolute",
            inset: 0,

            zIndex: 3,

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            opacity: 0,
            pointerEvents: "none",

            transition: "opacity 220ms ease",

            "&:focus-within": {
              opacity: 1,
              pointerEvents: "auto",
            },
          }}
        >
          <Box
            component="button"
            type="button"
            disabled={!playerUiState.ready}
            aria-label={
              playerUiState.playing
                ? lang === "ZH"
                  ? "暫停影片"
                  : lang === "JP"
                    ? "動画を一時停止"
                    : "Pause video"
                : lang === "ZH"
                  ? "播放影片"
                  : lang === "JP"
                    ? "動画を再生"
                    : "Play video"
            }
            onClick={handlePlayerInteraction}
            sx={{
              width: {
                xs: 62,
                sm: 72,
                md: 82,
              },

              height: {
                xs: 62,
                sm: 72,
                md: 82,
              },

              m: 0,
              p: 0,

              border: 0,
              borderRadius: "50%",

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              color: "#ffffff",

              backgroundColor: "rgba(0, 0, 0, 0.58)",

              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.28)",

              cursor: playerUiState.ready ? "pointer" : "default",

              appearance: "none",

              transition: "background-color 180ms ease, transform 180ms ease",

              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.76)",
                transform: "scale(1.04)",
              },

              "&:focus-visible": {
                outline: "3px solid rgba(255, 255, 255, 0.95)",
                outlineOffset: 3,
              },
            }}
          >
            {playerUiState.playing ? (
              <PauseRoundedIcon
                sx={{
                  fontSize: {
                    xs: 40,
                    sm: 48,
                    md: 56,
                  },
                }}
              />
            ) : (
              <PlayArrowRoundedIcon
                sx={{
                  fontSize: {
                    xs: 42,
                    sm: 50,
                    md: 58,
                  },

                  transform: "translateX(2px)",
                }}
              />
            )}
          </Box>

          <Box
            component="button"
            type="button"
            disabled={!playerUiState.ready}
            aria-label={
              playerUiState.muted
                ? lang === "ZH"
                  ? "開啟影片聲音"
                  : lang === "JP"
                    ? "動画の音声をオン"
                    : "Turn video sound on"
                : lang === "ZH"
                  ? "關閉影片聲音"
                  : lang === "JP"
                    ? "動画の音声をオフ"
                    : "Turn video sound off"
            }
            onClick={handleSoundToggle}
            sx={{
              position: "absolute",

              right: {
                xs: 12,
                md: 16,
              },

              bottom: {
                xs: 12,
                md: 16,
              },

              width: {
                xs: 42,
                md: 46,
              },

              height: {
                xs: 42,
                md: 46,
              },

              m: 0,
              p: 0,

              border: 0,
              borderRadius: "50%",

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              color: "#ffffff",
              backgroundColor: "rgba(0, 0, 0, 0.58)",

              boxShadow: "0 8px 22px rgba(0, 0, 0, 0.24)",

              cursor: playerUiState.ready ? "pointer" : "default",

              appearance: "none",

              transition: "background-color 180ms ease, transform 180ms ease",

              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.76)",
                transform: "scale(1.04)",
              },

              "&:focus-visible": {
                outline: "3px solid rgba(255, 255, 255, 0.95)",
                outlineOffset: 3,
              },
            }}
          >
            {playerUiState.muted ? (
              <VolumeOffRoundedIcon
                sx={{
                  fontSize: {
                    xs: 24,
                    md: 27,
                  },
                }}
              />
            ) : (
              <VolumeUpRoundedIcon
                sx={{
                  fontSize: {
                    xs: 24,
                    md: 27,
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function HandSanitizingManualPage({ lang }) {
  const sections = Array.isArray(handwashGuide.sections)
    ? handwashGuide.sections
    : [];

  return (
    <Container maxWidth="lg" sx={{ pb: { xs: 5, md: 7 } }}>
      <Box
        sx={{
          width: "100%",
          // border: "1px solid #d8e4ef",
          // borderRadius: 2,
          overflow: "hidden",
          bgcolor: "#fff",
          // boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Typography
            component="h1"
            sx={{
              color: getPrimaryColor("b2b"),
              fontWeight: 700,
              fontSize: { xs: 26, md: 34 },
              mb: 1.5,
            }}
          >
            {getText(handwashGuide.title, lang)}
          </Typography>

          <Typography
            sx={{
              color: getPrimaryColor("b2b"),
              fontWeight: 400,
              fontSize: { xs: 16, md: 18 },
              mb: 1.5,
            }}
          >
            {getText(handwashGuide.subtitle, lang)}
          </Typography>

          <Typography sx={{ fontSize: 15, lineHeight: 1.9, mb: 3 }}>
            {getText(handwashGuide.intro, lang)}
          </Typography>

          {/* YouTube video appears before the manual procedure. */}
          <ManualYouTubeVideoSection lang={lang} />

          <Box
            sx={{
              bgcolor: getPrimaryColor("b2b"),
              color: "#fff",
              borderRadius: 1,
              textAlign: "center",
              py: 1,
              mb: 3.5,
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            {getText(handwashGuide.procedureTitle, lang)}
          </Box>

          {sections.map((section) => (
            <ManualSection key={section.key} section={section} lang={lang} />
          ))}

          <FoodWorkerGuide lang={lang} />
          <ManualThumbnailImage lang={lang} />
        </Box>
      </Box>
    </Container>
  );
}

export default function EventInformationContent({ mode = "b2b" }) {
  const [lang, setLang] = useState(() => getLangFromCookie() || "EN");

  useEffect(() => {
    const handleLangChange = () => setLang(getLangFromCookie() || "EN");
    window.addEventListener("app:languageChanged", handleLangChange);
    return () =>
      window.removeEventListener("app:languageChanged", handleLangChange);
  }, []);

  const homeLabel = I18N.home[lang];
  const infoLabel = I18N.info[lang];
  const healthLabel = I18N.healthColumn[lang];

  const slug = useMemo(() => getSlugFromPathname(window.location.pathname), []);
  const newsType = "衛生資訊";
  const isManualSlug = useMemo(
    () => isHandSanitizingManual({ slug, news: null }),
    [slug],
  );

  const [loading, setLoading] = useState(!isManualSlug);
  const [errMsg, setErrMsg] = useState("");
  const [news, setNews] = useState(null);

  useEffect(() => {
    let alive = true;

    if (isManualSlug) {
      setLoading(false);
      setErrMsg("");
      setNews(null);

      return () => {
        alive = false;
      };
    }

    (async () => {
      try {
        setLoading(true);
        setErrMsg("");

        const data = await apiReadNews({ slug, newsType });
        if (!alive) return;

        setNews(data || null);
      } catch (e) {
        if (!alive) return;
        console.error("[EventInformationContent] read error:", e);
        setErrMsg("無法載入內容 / Failed to load content");
        setNews(null);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [slug, isManualSlug]);

  const titleLabel = news?.title || "";
  const contentHtml = useMemo(
    () => normalizeHtmlForRender(news?.content || ""),
    [news],
  );

  const useManualLayout =
    isManualSlug || isHandSanitizingManual({ slug, news });

  return (
    <Box>
      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link underline="hover" color="inherit" href="/">
            {homeLabel}
          </Link>

          <Link underline="hover" color="inherit" href="/最新資訊">
            {infoLabel}
          </Link>

          <Link underline="hover" color="inherit" href="/最新資訊/衛生資訊/">
            {healthLabel}
          </Link>

          <Typography color="text.primary">
            {loading ? "Loading..." : titleLabel || slug}
          </Typography>
        </Breadcrumbs>
      </Container>

      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={26} />
          </Box>
        )}

        {!loading && errMsg && <Alert severity="error">{errMsg}</Alert>}
      </Container>

      {!loading && !errMsg && useManualLayout && (
        <HandSanitizingManualPage lang={lang} />
      )}

      {!loading && !errMsg && !useManualLayout && (
        <Container maxWidth="md" sx={{ pb: { xs: 4, md: 6 } }}>
          <Box
            sx={{
              lineHeight: 1.8,
              "& p": { mt: 0, mb: 1.5 },
              "& img": {
                maxWidth: "100%",
                borderRadius: 2,
              },
              "& table": { width: "100%", borderCollapse: "collapse", my: 2 },
              "& td, & th": { border: "none", verticalAlign: "top", px: 1.25 },
              "& a": { wordBreak: "break-word" },
            }}
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />
        </Container>
      )}
    </Box>
  );
}
